import { forwardRef, type ReactNode } from 'react';
import {
  FunnelSimpleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XIcon,
  RowsIcon,
  SortAscendingIcon,
  SortDescendingIcon,
  CaretDownIcon,
  CaretRightIcon,
  CheckIcon,
  SlidersHorizontalIcon,
  type Icon,
} from '@phosphor-icons/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import {
  summarizeFilter,
  type FieldDef,
  type FieldOption,
  type SortSpec,
  type ViewFilter,
} from '@/shared/shell/view-controls';
import { menuItemClasses, menuLabelClasses } from '@/ui/menu-classes';
import classNames from '@/utils/classnames';

const MENU_CONTENT =
  'z-50 min-w-52 max-h-[min(60vh,420px)] overflow-y-auto flex flex-col gap-0.5 rounded-xl border border-line-strong bg-surface-2 p-1.5 shadow-menu animate-fade-in';

interface BarButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  filled?: boolean;
}

/** Pill-shaped bar control. forwardRef so it can be used as a Radix menu trigger via `asChild`. */
const BarButton = forwardRef<HTMLButtonElement, BarButtonProps>(function BarButton(
  { filled = false, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={classNames(
        'flex items-center gap-1.5 h-7 px-2.25 rounded-lg text-xs font-medium text-fg-secondary transition-colors cursor-pointer outline-none hover:text-fg data-[state=open]:text-fg',
        filled
          ? 'bg-surface-2 hover:bg-surface-3 data-[state=open]:bg-surface-3'
          : 'hover:bg-surface-3 data-[state=open]:bg-surface-3',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

function Dot({ color }: { color: string }) {
  return <span className="size-2 rounded-full shrink-0" style={{ backgroundColor: color }} aria-hidden />;
}

function Count({ value }: { value: number }) {
  return <span className="text-3xs text-fg-tertiary tabular-nums">{value}</span>;
}

/** A checkable value row shared by the add-filter submenus and the pill editor. */
function ValueItem({ option, checked, onToggle }: { option: FieldOption; checked: boolean; onToggle: () => void }) {
  return (
    <DropdownMenu.CheckboxItem
      checked={checked}
      onCheckedChange={onToggle}
      onSelect={(e) => e.preventDefault()}
      className={menuItemClasses}
    >
      <Dot color={option.color} />
      <span className="flex-1 truncate">{option.label}</span>
      <Count value={option.count} />
      {checked && <CheckIcon size={15} weight="bold" className="text-indigo-300" />}
    </DropdownMenu.CheckboxItem>
  );
}

/**
 * Field list whose entries expand into value submenus — pick a field and its values in one pass
 * (no add-then-reopen). Used by both the Filter and Add-filter buttons.
 */
function AddFilterMenu<F extends string>({
  trigger,
  filterFields,
  filters,
  fieldOptions,
  onToggleValue,
}: {
  trigger: ReactNode;
  filterFields: FieldDef<F>[];
  filters: ViewFilter<F>[];
  fieldOptions: Record<F, FieldOption[]>;
  onToggleValue: (field: F, value: string) => void;
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="start" sideOffset={6} className={MENU_CONTENT}>
          <div className={menuLabelClasses}>Filter by</div>
          {filterFields.map(({ field, label }) => {
            const selected = filters.find((f) => f.field === field)?.values ?? [];
            return (
              <DropdownMenu.Sub key={field}>
                <DropdownMenu.SubTrigger className={menuItemClasses}>
                  <span className="flex-1">{label}</span>
                  {selected.length > 0 && <Count value={selected.length} />}
                  <CaretRightIcon size={13} className="text-fg-tertiary" />
                </DropdownMenu.SubTrigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.SubContent sideOffset={4} alignOffset={-6} className={MENU_CONTENT}>
                    {fieldOptions[field].length === 0 ? (
                      <div className="px-2 py-1.5 text-2xs text-fg-tertiary">No values</div>
                    ) : (
                      fieldOptions[field].map((opt) => (
                        <ValueItem
                          key={opt.value}
                          option={opt}
                          checked={selected.includes(opt.value)}
                          onToggle={() => onToggleValue(field, opt.value)}
                        />
                      ))
                    )}
                  </DropdownMenu.SubContent>
                </DropdownMenu.Portal>
              </DropdownMenu.Sub>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function FilterPill<F extends string>({
  filter,
  filterFields,
  options,
  onChange,
  onRemove,
}: {
  filter: ViewFilter<F>;
  filterFields: FieldDef<F>[];
  options: FieldOption[];
  onChange: (patch: Partial<ViewFilter<F>>) => void;
  onRemove: () => void;
}) {
  const summary = summarizeFilter(filter, options, filterFields);

  function toggleValue(value: string) {
    const next = filter.values.includes(value) ? filter.values.filter((v) => v !== value) : [...filter.values, value];
    onChange({ values: next });
  }

  return (
    <div className="flex items-center h-7 rounded-lg bg-surface-3 border border-line-subtle">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 h-full pl-2.5 pr-2 rounded-l-lg text-2xs outline-none cursor-pointer"
          >
            <span className="text-fg-tertiary">{summary.field}</span>
            <span className="text-fg-tertiary">{summary.operator}</span>
            <span className="font-semibold text-fg">{summary.value}</span>
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content align="start" sideOffset={8} className={MENU_CONTENT}>
            <DropdownMenu.RadioGroup
              value={filter.operator}
              onValueChange={(op) => onChange({ operator: op as ViewFilter['operator'] })}
            >
              {(['is', 'is_not'] as const).map((op) => (
                <DropdownMenu.RadioItem key={op} value={op} className={menuItemClasses}>
                  <span className="flex-1">{op === 'is' ? 'is' : 'is not'}</span>
                  <DropdownMenu.ItemIndicator>
                    <CheckIcon size={15} weight="bold" className="text-indigo-300" />
                  </DropdownMenu.ItemIndicator>
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
            <div className="my-1 h-px bg-line-subtle" />
            {options.map((opt) => (
              <ValueItem
                key={opt.value}
                option={opt}
                checked={filter.values.includes(opt.value)}
                onToggle={() => toggleValue(opt.value)}
              />
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      <button
        type="button"
        title="Remove filter"
        onClick={onRemove}
        className="flex items-center justify-center h-full px-1.5 rounded-r-lg text-fg-tertiary transition-colors hover:text-fg cursor-pointer"
      >
        <XIcon size={12} />
      </button>
    </div>
  );
}

/** A simple single-select dropdown rendering a labelled radio list. */
function SelectMenu<T extends string>({
  trigger,
  label,
  value,
  options,
  onChange,
}: {
  trigger: ReactNode;
  label: string;
  value: T;
  options: FieldDef<T>[];
  onChange: (value: T) => void;
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={6} className={MENU_CONTENT}>
          <div className={menuLabelClasses}>{label}</div>
          <DropdownMenu.RadioGroup value={value} onValueChange={(v) => onChange(v as T)}>
            {options.map((opt) => (
              <DropdownMenu.RadioItem key={opt.field} value={opt.field} className={menuItemClasses}>
                <span className="flex-1">{opt.label}</span>
                <DropdownMenu.ItemIndicator>
                  <CheckIcon size={15} weight="bold" className="text-indigo-300" />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export interface DisplayToggle {
  label: string;
  checked: boolean;
  onToggle: () => void;
}

interface ViewBarProps<F extends string, G extends string, S extends string> {
  /** The fields offered in the Filter / Group by / Sort by menus. */
  filterFields: FieldDef<F>[];
  groupFields: FieldDef<G>[];
  sortFields: FieldDef<S>[];
  filters: ViewFilter<F>[];
  groupField: G;
  sort: SortSpec<S>;
  showEmptyGroups: boolean;
  fieldOptions: Record<F, FieldOption[]>;
  onToggleFilterValue: (field: F, value: string) => void;
  onChangeFilter: (id: string, patch: Partial<ViewFilter<F>>) => void;
  onRemoveFilter: (id: string) => void;
  onGroupChange: (field: G) => void;
  onSortChange: (sort: SortSpec<S>) => void;
  onToggleEmptyGroups: () => void;
  /** Extra checkboxes appended to the display-options menu, e.g. "Show archived sessions". */
  displayToggles?: DisplayToggle[];
  /** Free-text search. Omit both to hide the input. */
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
}

/** A bar button's contents: leading icon, label, and an optional dropdown caret. */
function BarButtonLabel({ icon: IconComp, label, caret = false }: { icon: Icon; label: string; caret?: boolean }) {
  return (
    <>
      <IconComp size={15} className="text-fg-tertiary" />
      {label}
      {caret && <CaretDownIcon size={13} className="text-fg-tertiary" />}
    </>
  );
}

/** Secondary bar of the main card (44px): filters left, grouping/sort right. */
export function ViewBar<F extends string, G extends string, S extends string>({
  filterFields,
  groupFields,
  sortFields,
  filters,
  groupField,
  sort,
  showEmptyGroups,
  fieldOptions,
  onToggleFilterValue,
  onChangeFilter,
  onRemoveFilter,
  onGroupChange,
  onSortChange,
  onToggleEmptyGroups,
  displayToggles,
  search,
  onSearchChange,
  searchPlaceholder = 'Search',
}: ViewBarProps<F, G, S>) {
  const groupLabel = groupFields.find((g) => g.field === groupField)?.label ?? '';
  const sortLabel = sortFields.find((s) => s.field === sort.field)?.label ?? '';
  const SortIcon = sort.dir === 'asc' ? SortAscendingIcon : SortDescendingIcon;

  return (
    <div className="flex items-center gap-2 h-11 px-4 border-b border-line-subtle shrink-0">
      <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-x-auto">
        <AddFilterMenu
          trigger={
            <BarButton filled>
              <BarButtonLabel icon={FunnelSimpleIcon} label="Filter" />
            </BarButton>
          }
          filterFields={filterFields}
          filters={filters}
          fieldOptions={fieldOptions}
          onToggleValue={onToggleFilterValue}
        />

        {filters.map((filter) => (
          <FilterPill
            key={filter.id}
            filter={filter}
            filterFields={filterFields}
            options={fieldOptions[filter.field]}
            onChange={(patch) => onChangeFilter(filter.id, patch)}
            onRemove={() => onRemoveFilter(filter.id)}
          />
        ))}

        <AddFilterMenu
          trigger={
            <BarButton className="font-normal">
              <BarButtonLabel icon={PlusIcon} label="Add filter" />
            </BarButton>
          }
          filterFields={filterFields}
          filters={filters}
          fieldOptions={fieldOptions}
          onToggleValue={onToggleFilterValue}
        />
      </div>

      {onSearchChange && (
        <div className="relative shrink-0">
          <MagnifyingGlassIcon
            size={14}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-tertiary"
          />
          <input
            type="search"
            value={search ?? ''}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="h-7 w-44 rounded-lg bg-surface-2 pl-8 pr-2.5 text-xs text-fg outline-none transition-colors placeholder:text-fg-tertiary hover:bg-surface-3 focus:bg-surface-3"
          />
        </div>
      )}

      <SelectMenu
        trigger={
          <BarButton>
            <BarButtonLabel icon={RowsIcon} label={`Group: ${groupLabel}`} caret />
          </BarButton>
        }
        label="Group by"
        value={groupField}
        options={groupFields}
        onChange={onGroupChange}
      />

      <SelectMenu
        trigger={
          <BarButton>
            <BarButtonLabel icon={SortIcon} label={sortLabel} caret />
          </BarButton>
        }
        label="Sort by"
        value={sort.field}
        options={sortFields}
        onChange={(field) => onSortChange({ ...sort, field })}
      />

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            title="Display options"
            className="flex items-center justify-center h-7 w-7.5 rounded-lg bg-surface-2 text-fg-tertiary transition-colors hover:text-fg hover:bg-surface-3 data-[state=open]:text-fg data-[state=open]:bg-surface-3 cursor-pointer outline-none"
          >
            <SlidersHorizontalIcon size={15} />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content align="end" sideOffset={6} className={MENU_CONTENT}>
            <div className={menuLabelClasses}>Ordering</div>
            <DropdownMenu.RadioGroup
              value={sort.dir}
              onValueChange={(dir) => onSortChange({ ...sort, dir: dir as SortSpec['dir'] })}
            >
              {(
                [
                  ['asc', 'Ascending'],
                  ['desc', 'Descending'],
                ] as const
              ).map(([dir, dirLabel]) => (
                <DropdownMenu.RadioItem key={dir} value={dir} className={menuItemClasses}>
                  <span className="flex-1">{dirLabel}</span>
                  <DropdownMenu.ItemIndicator>
                    <CheckIcon size={15} weight="bold" className="text-indigo-300" />
                  </DropdownMenu.ItemIndicator>
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
            <div className="my-1 h-px bg-line-subtle" />
            <DropdownMenu.CheckboxItem
              checked={showEmptyGroups}
              onCheckedChange={onToggleEmptyGroups}
              onSelect={(e) => e.preventDefault()}
              className={menuItemClasses}
            >
              <span className="flex-1">Show empty groups</span>
              {showEmptyGroups && <CheckIcon size={15} weight="bold" className="text-indigo-300" />}
            </DropdownMenu.CheckboxItem>
            {displayToggles?.map((toggle) => (
              <DropdownMenu.CheckboxItem
                key={toggle.label}
                checked={toggle.checked}
                onCheckedChange={toggle.onToggle}
                onSelect={(e) => e.preventDefault()}
                className={menuItemClasses}
              >
                <span className="flex-1">{toggle.label}</span>
                {toggle.checked && <CheckIcon size={15} weight="bold" className="text-indigo-300" />}
              </DropdownMenu.CheckboxItem>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
